import { AxiosError } from 'axios';

import { TableView } from '@components/table';

import { request } from 'api/request';
import { BulkCreateTraveller } from 'api/traveller-journeys/types';
import { ListResponse, TravellerAttribute } from 'api/types';

export type BulkErrorResponse = AxiosError<{
  travellers?: { [key: string]: string[] }[];
}>;

export type CompanyTravellersUserSettings = {
  settings: {
    viewState?: TableView;
  } | null;
};

export type CompanyTraveller = {
  id: number;
  shortId: string;
  name: string;
  email: string;
  phone: string;
  picture: string;
  company: string;
  companyTravellerAttributes: TravellerAttribute[];
  dateCreated: string;
  numJourneys: number;
  travellerJourneys: string[];
  travellerJourneyIds: string[];
};

type CompanyTravellerDataResult = ListResponse<CompanyTraveller>;

export type BulkAddToJourneyStatsType = {
  totalTravellers: number;
  alreadyExists: number;
  notExists: number;
};

type BulkCreateAttributes = {
  [K in `journey_attribute__${string}`]: string;
};
type BulkCreateCompanyTravellers = {
  [K in `company_traveller_${string}`]: string;
};

export type BulkAddToJourneyType = BulkCreateAttributes &
  BulkCreateCompanyTravellers & {
    journeyId: string;
    allowDuplicate?: boolean;
  };

export const loadCompanyTravellers = async (queryString?: string) => {
  const response = await request.get<CompanyTravellerDataResult>(
    `v2/company-travellers/${queryString}`
  );
  const data = response.data;

  data.results = data.results.map((traveller) => ({
    ...traveller,
    numJourneys: traveller.travellerJourneys.length ?? 0,
  }));
  return data;
};

export const loadCompanyTravellersFull = async () => {
  const response = await request.get<Array<CompanyTraveller>>('v2/company-travellers/full/');
  return response.data.map((traveller) => ({
    ...traveller,
    numJourneys: traveller.travellerJourneys.length ?? 0,
  }));
};

export const loadCompanyTravellerSettings = async () => {
  const response = await request.get<CompanyTravellersUserSettings>(
    'v2/company-travellers/user-settings/'
  );
  return response.data.settings;
};

export const updateCompanyTravellerUserSettings = async (data: CompanyTravellersUserSettings) => {
  const response = await request.patch('v2/company-travellers/user-settings/', data);
  return response.data;
};

export const getCompanyTraveller = async (travellerShortId: string) => {
  const response = await request.get<CompanyTraveller>(
    `v1/company-travellers/${travellerShortId}/`
  );
  return response.data;
};

export const CreateCompanyTraveller = async (data: FormData) => {
  const response = await request.post<CompanyTraveller>(`v1/company-travellers/`, data);
  return response.data;
};

export const editCompanyTraveller = async (travellerShortId: string, data: FormData) => {
  const response = await request.patch(`v1/company-travellers/${travellerShortId}/`, data);
  return response.data;
};

export const deleteCompanyTraveller = async (shortID: string) => {
  const response = await request.delete(`v1/company-travellers/${shortID}/`);
  return response.data;
};

export async function getBulkCreateTemplate() {
  const response = await request.get(`v1/company-travellers/bulk-create-template/`, {
    responseType: 'blob',
  });
  return response.data;
}

export const bulkCreateCompanyTraveller = async (data: {
  travellers: BulkCreateTraveller[];
  isSave: boolean;
  updateExisting?: boolean;
}) => {
  const response = await request.post(`v1/company-travellers/bulk-create/`, data);
  return response.data;
};

export const bulkDeleteCompanyTraveller = async (data: { ids: string[]; selectAll: boolean }) => {
  const response = await request.post('v1/company-travellers/bulk-delete/', data);
  return response.data;
};

export const bulkAddCompanyTravellerToJourney = async (data: BulkAddToJourneyType) => {
  const response = await request.postForm(`v1/company-travellers/bulk-add-to-journey/`, data);
  return response.data;
};

export const bulkAddCompanyTravellerToJourneyStatistics = async (
  journeyId: string,
  ids: string[]
) => {
  const response = await request.post<BulkAddToJourneyStatsType>(
    'v1/company-travellers/bulk-add-to-journey-stats/',
    { journeyId, ids }
  );
  return response.data;
};
